import { getDay, fromUnixTime, getUnixTime, startOfDay } from 'date-fns';
import { timeUnix } from '../../utility/k_time';

export interface IAvailability {
    start: number;
    end: number;
    dow: number;
    active: boolean;
}

interface IRange {
    start: number;
    end: number;
}

export interface ITime {
    start: string;
    end: string;
}

export default class DateService {
    public static getInstance() {
        if (!this.instance) {
            this.instance = new DateService();
        }

        return this.instance;
    }

    private static instance: DateService;

    private serverTimeDiff: number = 0;

    private targetUnix: number = 0;

    public setTargetUnix(time: number) {
        this.targetUnix = time;
    }

    public setServerTime(time: number) {
        if (!time) {
            this.serverTimeDiff = 0;
            return;
        }
        const now = timeUnix();
        this.serverTimeDiff = Math.round(time / 1000) - now;
    }

    public getDay() {
        return getDay(this.getNow());
    }

    public getUnix() {
        return getUnixTime(this.getNow());
    }

    public getDateFromUnix(unix: number) {
        return fromUnixTime(unix + this.serverTimeDiff);
    }

    public getStartOfDayUnix() {
        return getUnixTime(startOfDay(this.getNow()));
    }

    public isAvailable(list: IAvailability[]): boolean {
        if (!list || list?.length === 0) {
            return true;
        }

        const day = this.getDay();
        const startOfUnix = this.getStartOfDayUnix();
        const nowUnix = this.getUnix();
        const time = nowUnix - startOfUnix;

        return list?.some((item) => {
            if (!item.active) {
                return false;
            }
            if (item.dow !== day) {
                return false;
            }
            if (item.start <= time && time <= item.end) {
                return true;
            }
            return false;
        });
    }

    public getAvailableRanges(list: IAvailability[]): Array<{ from: string; to: string }> {
        if (!list || list?.length === 0) {
            return [];
        }

        const day = this.getDay();
        return (
            list
                ?.filter((item) => {
                    return item.dow === day && item.active;
                })
                .map((item) => {
                    return { from: this.getTime(item.start), to: this.getTime(item.end) };
                }) || []
        );
    }

    public getAvailableRangesNum(list: IAvailability[]): Array<{ from: number; to: number }> {
        if (!list || list?.length === 0) {
            return [];
        }

        const day = this.getDay();
        return (
            list
                ?.filter((item) => {
                    return item.dow === day && item.active;
                })
                .map((item) => {
                    return { from: item.start, to: item.end };
                }) || []
        );
    }

    public getNow() {
        if (this.targetUnix) {
            return fromUnixTime(this.targetUnix + this.serverTimeDiff);
        }

        return fromUnixTime(timeUnix() + this.serverTimeDiff);
    }

    public getTime(unix: number) {
        const date = new Date(0);
        date.setSeconds(unix); // specify value for SECONDS here
        return date.toISOString().substring(11, 16);
    }

    public getUnixTime(time?: string) {
        if (!time) {
            return undefined;
        }
        const [hour, min, sec] = time.split(':');

        const stringToUnix = (str?: string) => {
            if (!str) {
                return 0;
            }
            if (isNaN(Number(str))) {
                return 0;
            }
            return Number(str);
        };
        return (stringToUnix(hour) * 60 + stringToUnix(min)) * 60 + stringToUnix(sec);
    }

    private getPeriods(ranges: IAvailability[]): IRange[] {
        return ranges.map((r) => ({
            start: r.start,
            end: r.end,
        }));
    }

    private getOverlapRanges(range1: IRange[], range2: IRange[]): IRange[] {
        const overlapRanges: IRange[] = [];
        range1.forEach((period1) => {
            range2.forEach((period2) => {
                if (period1.start <= period2.end && period2.start <= period1.end) {
                    const overlapStart = period1.start < period2.start ? period2.start : period1.start;
                    const overlapEnd = period1.end < period2.end ? period1.end : period2.end;
                    overlapRanges.push({ start: overlapStart, end: overlapEnd });
                }
            });
        });
        return overlapRanges;
    }

    public getOverlap(availabilityList: IAvailability[][], dow: number): IRange[] {
        let isInactive = false;
        const ranges = availabilityList.reduce<IRange[][]>((acc, range) => {
            const filteredRanges = range.filter((o) => o.dow === dow);
            const periods = this.getPeriods(filteredRanges);
            if (periods.length > 0) {
                acc.push(periods);
            }
            if (filteredRanges.some((o) => !o.active)) {
                isInactive = true;
            }
            return acc;
        }, []);
        if (isInactive) {
            return [];
        }
        if (ranges.length === 1) {
            return ranges[0] || [];
        }
        const initRange = ranges[0];
        ranges.splice(0, 1);
        return ranges.reduce((acc, curr) => {
            return this.getOverlapRanges(acc, curr);
        }, initRange);
    }

    public getOverlapTime(availabilityList: IAvailability[][]): ITime[] {
        const dow = this.getDay();
        const ranges = this.getOverlap(availabilityList, dow);
        if (!ranges?.length) {
            return [];
        }
        return ranges.map((range) => {
            return {
                start: this.getTime(range.start),
                end: this.getTime(range.end),
            };
        });
    }
}
